// ResumePDF.tsx
// Drop-in react-pdf renderer for all 10 resume templates.
// Install dependency first:  npm install @react-pdf/renderer
//
// Usage in App.tsx:
//   import { downloadResumePDF } from './components/ResumePDF';
//   // replace the old handleDownloadPDF with:
//   const handleDownloadPDF = () => downloadResumePDF(form, template, customColor);

import React from 'react';
import {
  Document, Page, View, Text, Image, Font, pdf, Svg, Path,
} from '@react-pdf/renderer';
import { FONT_STYLES, TEMPLATES } from '../data/initialData';
import { ICON_PATHS, ContactType } from './resumeIcons';
import ibmPlexSans400 from '@fontsource/ibm-plex-sans/files/ibm-plex-sans-latin-400-normal.woff?url';
import ibmPlexSans400Italic from '@fontsource/ibm-plex-sans/files/ibm-plex-sans-latin-400-italic.woff?url';
import ibmPlexSans500 from '@fontsource/ibm-plex-sans/files/ibm-plex-sans-latin-500-normal.woff?url';
import ibmPlexSans600 from '@fontsource/ibm-plex-sans/files/ibm-plex-sans-latin-600-normal.woff?url';
import ibmPlexSans700 from '@fontsource/ibm-plex-sans/files/ibm-plex-sans-latin-700-normal.woff?url';

// ─── Types ────────────────────────────────────────────────────────────────────
interface PersonalData {
  name: string;
  email: string;
  phone: string;
  location: string;
  linkedin: string;
  github: string;
  portfolio: string;
  summary: string;
  photo: string | null;
}

interface EducationEntry {
  id: number;
  school: string;
  degree: string;
  field: string;
  gpa: string;
  percentage: string;
  startYear: string;
  endYear: string;
  current: boolean;
  courses: string;
  honors: string;
}

interface ExperienceEntry {
  id: number;
  company: string;
  role: string;
  location: string;
  startDate: string;
  endDate: string;
  current: boolean;
  bullets: string;
}

interface ProjectEntry {
  id: number;
  name: string;
  tech: string;
  link: string;
  github: string;
  description: string;
}

interface SkillsData {
  technical: string;
  frameworks: string;
  languages: string;
  databases: string;
  cloud: string;
  soft: string;
}

interface CertEntry {
  id: number;
  name: string;
  issuer: string;
  date: string;
  credentialId: string;
}

interface CustomSection {
  id: number;
  title: string;
  items: string;
}

interface ResumeFormData {
  personal: PersonalData;
  education: EducationEntry[];
  experience: ExperienceEntry[];
  projects: ProjectEntry[];
  skills: SkillsData;
  certifications: CertEntry[];
  customSections: CustomSection[];
}

interface FontSet {
  body: string;
  heading: string;
}

interface DividerProps {
  color: string;
  marginTop?: number;
  marginBottom?: number;
  thickness?: number;
}

interface SectionHeadingProps {
  label: string;
  accent: string;
  fonts: FontSet;
  style?: Record<string, unknown>;
  templateStyle?: string;
}

interface EntryRowProps {
  title: string;
  date?: string;
  accent: string;
  fonts: FontSet;
}

interface SubLineProps {
  text: string;
  fonts: FontSet;
}

interface BulletListProps {
  text: string;
  fonts: FontSet;
}

interface SkillTagsProps {
  items: string;
  accent: string;
  fonts: FontSet;
}

interface SkillRowProps {
  label: string;
  items: string;
  accent: string;
  fonts: FontSet;
}

interface ContactLineProps {
  // Each item has a contact type (drives the icon) and the value to display.
  items: { type: ContactType; value: string }[];
  fonts: FontSet;
  color?: string;
  fontSize?: number;
  iconColor?: string;
}

interface SectionProps {
  accent: string;
  fonts: FontSet;
  templateStyle?: string;
}

interface EducationSectionProps extends SectionProps { templateStyle?: string; education: EducationEntry[]; }
interface ExperienceSectionProps extends SectionProps { templateStyle?: string; experience: ExperienceEntry[]; }
interface ProjectsSectionProps extends SectionProps { templateStyle?: string; projects: ProjectEntry[]; }
interface SkillsSectionProps extends SectionProps { templateStyle?: string; skills: SkillsData; }
interface CertsSectionProps extends SectionProps { templateStyle?: string; certifications: CertEntry[]; }
interface TemplateProps {
  data: ResumeFormData;
  accent: string;
  fonts: FontSet;
  style?: string;
}

interface ResumeDocumentProps {
  data: ResumeFormData;
  templateId: string;
  customColor: string | null;
  fontStyleId?: string;
}

// ─── Font setup ──────────────────────────────────────────────────────────────
// Use ONLY PDF built-in fonts — zero network requests, zero 404/Buffer errors.
// Available built-ins: Helvetica, Times-Roman, Courier (and their Bold/Oblique variants).
Font.registerHyphenationCallback((word: string) => [word]);

Font.register({
  family: 'IBM Plex Sans',
  fonts: [
    { src: ibmPlexSans400, fontWeight: 400 },
    { src: ibmPlexSans400Italic, fontWeight: 400, fontStyle: 'italic' },
    { src: ibmPlexSans500, fontWeight: 500 },
    { src: ibmPlexSans600, fontWeight: 600 },
    { src: ibmPlexSans700, fontWeight: 700 },
  ],
});

// ─── Template → font mapping ─────────────────────────────────────────────────
// Always return base PDF family names (Helvetica / Times-Roman / Courier).
// @react-pdf/renderer picks the correct built-in variant (Bold / Italic /
// BoldItalic) at render time based on the element's fontWeight / fontStyle.
// Returning variant names like "Times-BoldItalic" together with explicit
// fontWeight + fontStyle causes "Could not resolve font" errors because no
// font is registered under that variant-as-family combination.
function getFonts(templateId: string, fontStyleId = 'template'): FontSet {
  const selectedFont = FONT_STYLES.find((font) => font.id === fontStyleId);
  if (selectedFont?.pdfFonts) return selectedFont.pdfFonts;

  switch (templateId) {
    case 'classic':
    case 'executive': return { body: 'Times-Roman', heading: 'Times-Roman' };
    case 'elegant':   return { body: 'Times-Roman', heading: 'Times-Roman' };
    case 'tech':      return { body: 'Helvetica', heading: 'Courier' };
    default:          return { body: 'Helvetica', heading: 'Helvetica' };
  }
}

// ─── Colour helpers ───────────────────────────────────────────────────────────
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return { r, g, b };
}
function withAlpha(hex: string, alpha: number): string {
  // react-pdf doesn't support rgba — simulate with a light tint on white
  const { r, g, b } = hexToRgb(hex);
  const a = alpha;
  return `rgb(${Math.round(r * a + 255 * (1 - a))},${Math.round(g * a + 255 * (1 - a))},${Math.round(b * a + 255 * (1 - a))})`;
}

// ─── Shared primitive components ──────────────────────────────────────────────
function Divider({ color, marginTop = 4, marginBottom = 8, thickness = 1.5 }: DividerProps) {
  return <View style={{ borderBottomWidth: thickness, borderBottomColor: color, marginTop, marginBottom }} />;
}

function SectionHeading({ label, accent, fonts, style: extraStyle = {}, templateStyle }: SectionHeadingProps) {
  if (templateStyle === 'creative') {
    return (
      <View style={{ marginBottom: 4, flexDirection: 'row', alignItems: 'stretch', ...extraStyle }}>
        <View style={{ width: 3, backgroundColor: accent, borderRadius: 1, marginRight: 6 }} />
        <Text style={{
          fontSize: 9,
          fontFamily: fonts.heading,
          fontWeight: 700,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          color: accent,
          paddingVertical: 2,
        }}>{label}</Text>
      </View>
    );
  }
  if (templateStyle === 'elegant') {
    return (
      <View style={{ marginBottom: 5, alignItems: 'center', ...extraStyle }}>
        <View style={{ borderTopWidth: 1, borderBottomWidth: 1, borderTopColor: accent, borderBottomColor: accent, paddingVertical: 2, width: '100%' }}>
          <Text style={{
            fontSize: 9,
            fontFamily: fonts.heading,
            fontWeight: 700,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: accent,
            textAlign: 'center',
          }}>{label}</Text>
        </View>
      </View>
    );
  }
  return (
    <View style={{ marginBottom: 4, ...extraStyle }}>
      <Text style={{
        fontSize: 9,
        fontFamily: fonts.heading,
        fontWeight: 700,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        color: accent,
        paddingBottom: 3,
        borderBottomWidth: 1.5,
        borderBottomColor: accent,
      }}>{label}</Text>
    </View>
  );
}

function EntryRow({ title, date, accent, fonts }: EntryRowProps) {
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 1 }}>
      <Text style={{ fontSize: 9.5, fontFamily: fonts.body, fontWeight: 700, color: '#111', flex: 1 }}>{title}</Text>
      {date ? <Text style={{ fontSize: 9, color: '#555', fontFamily: fonts.body, flexShrink: 0 }}>{date}</Text> : null}
    </View>
  );
}

function SubLine({ text, fonts }: SubLineProps) {
  if (!text) return null;
  return <Text style={{ fontSize: 9.5, color: '#444', fontFamily: fonts.body, marginBottom: 2 }}>{text}</Text>;
}

function BulletList({ text, fonts }: BulletListProps) {
  if (!text) return null;
  const lines = text.split('\n').filter(l => l.trim()).map(l => l.replace(/^[-•*]\s*/, ''));
  return (
    <View style={{ marginTop: 1 }}>
      {lines.map((line, i) => (
        <View key={i} style={{ flexDirection: 'row', marginBottom: 1.5 }}>
          <Text style={{ fontSize: 9, color: '#333', fontFamily: fonts.body, marginRight: 4, marginTop: 1 }}>•</Text>
          <Text style={{ fontSize: 9.5, color: '#222', fontFamily: fonts.body, flex: 1, lineHeight: 1.35 }}>{line}</Text>
        </View>
      ))}
    </View>
  );
}

function SkillTags({ items, accent, fonts }: SkillTagsProps) {
  if (!items) return null;
  const tags = items.split(',').map(s => s.trim()).filter(Boolean);
  const bgColor = withAlpha(accent, 0.12);
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 2 }}>
      {tags.map((tag, i) => (
        <View key={i} style={{
          backgroundColor: bgColor,
          borderRadius: 3,
          borderWidth: 1,
          borderColor: withAlpha(accent, 0.45),
          paddingHorizontal: 5,
          paddingVertical: 1.5,
        }}>
          <Text style={{ fontSize: 8.5, color: accent, fontFamily: fonts.body, fontWeight: 500 }}>{tag}</Text>
        </View>
      ))}
    </View>
  );
}

function SkillRow({ label, items, accent, fonts }: SkillRowProps) {
  if (!items) return null;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 3 }}>
      <Text style={{ fontSize: 9, fontFamily: fonts.body, fontWeight: 700, color: '#333', width: 80, flexShrink: 0, paddingTop: 3 }}>{label}</Text>
      <View style={{ flex: 1 }}>
        <SkillTags items={items} accent={accent} fonts={fonts} />
      </View>
    </View>
  );
}

// ─── Contact icons (PDF) ─────────────────────────────────────────────────────
// Renders one of the shared icon paths as a vector glyph using
// @react-pdf/renderer's SVG primitives — this is the only reliable way to
// get real icons in the PDF (the built-in fonts have no emoji glyphs and
// registering a custom icon font adds bundle weight for one use case).
function PdfIcon({ type, size = 9, color = '#666' }: { type: ContactType; size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path d={ICON_PATHS[type]} fill={color} />
    </Svg>
  );
}

function ContactLine({ items, fonts, color = '#444', fontSize = 9, iconColor }: ContactLineProps) {
  if (!items.length) return null;
  const ic = iconColor || color;
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
      {items.map((item, i) => (
        <View key={i} style={{ flexDirection: 'row', alignItems: 'center', marginRight: 8, marginBottom: 2 }}>
          <View style={{ marginRight: 3, marginTop: 0.5 }}>
            <PdfIcon type={item.type} size={fontSize + 1} color={ic} />
          </View>
          <Text style={{ fontSize, color, fontFamily: fonts.body }}>{item.value}</Text>
        </View>
      ))}
    </View>
  );
}

// ─── Section renderers ────────────────────────────────────────────────────────
// All section parent Views use `wrap={false}` so a section is never split
// across a page break. If a section doesn't fit on the current page it moves
// entirely to the next page, instead of being chopped mid-row (which is what
// caused the Bold template's "Soft Skills:" row to land flush against the top
// of page 2 and look cut off).
function EducationSection({ templateStyle, education, accent, fonts }: EducationSectionProps) {
  const items = education.filter(e => e.school);
  if (!items.length) return null;
  return (
    <View wrap={false} style={{ marginBottom: 7 }}>
      <SectionHeading label="Education" accent={accent} fonts={fonts} templateStyle={templateStyle} />
      {items.map((ed, i) => (
        <View key={i} wrap={false} style={{ marginBottom: 4 }}>
          <EntryRow
            title={ed.school}
            date={`${ed.startYear || ''}${ed.current ? ' – Present' : ed.endYear ? ` – ${ed.endYear}` : ''}`}
            accent={accent} fonts={fonts}
          />
          <SubLine text={[ed.degree, ed.field ? `— ${ed.field}` : '', ed.gpa ? `GPA: ${ed.gpa}` : '', ed.percentage ? `Percentage: ${ed.percentage.replace(/%$/, '')}%` : '', ed.honors].filter(Boolean).join(' | ')} fonts={fonts} />
          {ed.courses ? <Text style={{ fontSize: 9, color: '#555', fontFamily: fonts.body }}><Text style={{ fontWeight: 700 }}>Courses: </Text>{ed.courses}</Text> : null}
        </View>
      ))}
    </View>
  );
}

function ExperienceSection({ templateStyle, experience, accent, fonts }: ExperienceSectionProps) {
  const items = experience.filter(e => e.company || e.role);
  if (!items.length) return null;
  return (
    <View wrap={false} style={{ marginBottom: 7 }}>
      <SectionHeading label="Experience" accent={accent} fonts={fonts} templateStyle={templateStyle} />
      {items.map((ex, i) => (
        <View key={i} wrap={false} style={{ marginBottom: 5 }}>
          <EntryRow
            title={ex.role}
            date={`${ex.startDate || ''}${ex.current ? ' – Present' : ex.endDate ? ` – ${ex.endDate}` : ''}`}
            accent={accent} fonts={fonts}
          />
          <SubLine text={[ex.company, ex.location].filter(Boolean).join(' | ')} fonts={fonts} />
          <BulletList text={ex.bullets} fonts={fonts} />
        </View>
      ))}
    </View>
  );
}

function ProjectsSection({ templateStyle, projects, accent, fonts }: ProjectsSectionProps) {
  const items = projects.filter(p => p.name);
  if (!items.length) return null;
  return (
    <View wrap={false} style={{ marginBottom: 7 }}>
      <SectionHeading label="Projects" accent={accent} fonts={fonts} templateStyle={templateStyle} />
      {items.map((p, i) => (
        <View key={i} wrap={false} style={{ marginBottom: 5 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 1 }}>
            <Text style={{ fontSize: 9.5, fontFamily: fonts.body, fontWeight: 700, color: '#111', flex: 1 }}>
              {p.name}{p.tech ? <Text style={{ fontWeight: 400, color: accent, fontSize: 9.5 }}> — {p.tech}</Text> : ''}
            </Text>
            {(p.github || p.link) ? (
              <Text style={{ fontSize: 8.5, color: '#555', fontFamily: fonts.body, flexShrink: 0 }}>
                {[p.github, p.link].filter(Boolean).join(' | ')}
              </Text>
            ) : null}
          </View>
          {p.description ? <Text style={{ fontSize: 9.5, color: '#333', fontFamily: fonts.body, lineHeight: 1.35 }}>{p.description}</Text> : null}
        </View>
      ))}
    </View>
  );
}

function SkillsSection({ templateStyle, skills, accent, fonts }: SkillsSectionProps) {
  const hasSkills = skills.languages || skills.frameworks || skills.databases || skills.cloud || skills.technical || skills.soft;
  if (!hasSkills) return null;
  return (
    <View wrap={false} style={{ marginBottom: 7 }}>
      <SectionHeading label="Skills" accent={accent} fonts={fonts} templateStyle={templateStyle} />
      <SkillRow label="Languages:"    items={skills.languages}  accent={accent} fonts={fonts} />
      <SkillRow label="Frameworks:"   items={skills.frameworks} accent={accent} fonts={fonts} />
      <SkillRow label="Databases:"    items={skills.databases}  accent={accent} fonts={fonts} />
      <SkillRow label="Cloud & DevOps:" items={skills.cloud}    accent={accent} fonts={fonts} />
      <SkillRow label="Other:"        items={skills.technical}  accent={accent} fonts={fonts} />
      {skills.soft ? (
        <View wrap={false} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 3 }}>
          <Text style={{ fontSize: 9, fontFamily: fonts.body, fontWeight: 700, color: '#333', width: 80, flexShrink: 0 }}>Soft Skills:</Text>
          <Text style={{ fontSize: 9.5, color: '#333', fontFamily: fonts.body, flex: 1 }}>{skills.soft}</Text>
        </View>
      ) : null}
    </View>
  );
}

function CertsSection({ templateStyle, certifications, accent, fonts }: CertsSectionProps) {
  const items = certifications.filter(c => c.name);
  if (!items.length) return null;
  return (
    <View wrap={false} style={{ marginBottom: 7 }}>
      <SectionHeading label="Certifications" accent={accent} fonts={fonts} templateStyle={templateStyle} />
      {items.map((c, i) => (
        <View key={i} wrap={false} style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 9.5, fontFamily: fonts.body, fontWeight: 700, color: '#111' }}>
              {c.name}{c.issuer ? <Text style={{ fontWeight: 400, color: '#555' }}> — {c.issuer}</Text> : ''}
              {c.credentialId ? <Text style={{ fontSize: 8.5, color: '#777' }}> (ID: {c.credentialId})</Text> : ''}
            </Text>
          </View>
          {c.date ? <Text style={{ fontSize: 9, color: '#555', fontFamily: fonts.body, flexShrink: 0 }}>{c.date}</Text> : null}
        </View>
      ))}
    </View>
  );
}

function CustomSectionsSection({ templateStyle, customSections, accent, fonts }: SectionProps & { customSections: CustomSection[] }) {
  if (!customSections || !customSections.length) return null;
  const filled = customSections.filter(s => s.title && s.items);
  if (!filled.length) return null;
  return (
    <>
      {filled.map((sec, i) => (
        <View key={i} wrap={false} style={{ marginBottom: 7 }}>
          <SectionHeading label={sec.title} accent={accent} fonts={fonts} templateStyle={templateStyle} />
          <BulletList text={sec.items} fonts={fonts} />
        </View>
      ))}
    </>
  );
}

// ─── Template: DEFAULT (classic / modern / minimal / executive / tech / creative / compact) ──
function DefaultTemplate({ data, accent, fonts, style }: TemplateProps) {
  const { personal, education, experience, projects, skills, certifications, customSections } = data;
  const contactItems = [
    personal.email    && { type: 'email' as const,    value: personal.email },
    personal.phone    && { type: 'phone' as const,    value: personal.phone },
    personal.location && { type: 'location' as const, value: personal.location },
    personal.linkedin && { type: 'linkedin' as const, value: personal.linkedin },
    personal.github   && { type: 'github' as const,   value: personal.github },
    personal.portfolio && { type: 'portfolio' as const, value: personal.portfolio },
  ].filter(Boolean) as { type: ContactType; value: string }[];

  const isCompact = style === 'compact';
  const pad = isCompact ? 24 : 32;
  const nameSz = style === 'minimal' ? 18 : style === 'compact' ? 16 : style === 'tech' ? 18 : 20;

  return (
    <Page size="A4" style={{ fontFamily: fonts.body, paddingLeft: style === 'creative' ? pad + 8 : pad, paddingRight: pad, paddingTop: 24, paddingBottom: 24, backgroundColor: '#fff' }}>
      {/* Creative left border */}
      {style === 'creative' && (
        <View style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, backgroundColor: accent }} />
      )}

      {/* Header */}
      <View style={{ borderBottomWidth: 2, borderBottomColor: accent, paddingBottom: 8, marginBottom: 8, flexDirection: personal.photo ? 'row' : 'column', alignItems: 'flex-start', gap: personal.photo ? 12 : 0 }}>
        {personal.photo ? (
          <View style={{ width: 64, height: 64, borderRadius: 32, overflow: 'hidden', flexShrink: 0, marginTop: 2 }}>
          <Image src={personal.photo} style={{ width: 64, height: 64 }} />
        </View>
        ) : null}
        <View style={personal.photo ? { flex: 1 } : {}}>
          <Text style={{ fontSize: nameSz, fontFamily: fonts.heading, fontWeight: 700, color: accent, marginBottom: 4, letterSpacing: style === 'minimal' ? 1 : 0, textTransform: style === 'minimal' ? 'uppercase' : 'none', fontStyle: style === 'executive' ? 'italic' : 'normal' }}>
            {personal.name || 'Your Name'}
          </Text>
          <ContactLine items={contactItems} fonts={fonts} />
          {personal.summary ? (
            <Text style={{ fontSize: 9.5, color: '#333', fontFamily: fonts.body, marginTop: 4, lineHeight: 1.4 }}>{personal.summary}</Text>
          ) : null}
        </View>
      </View>

      <EducationSection templateStyle={style}   education={education}           accent={accent} fonts={fonts} />
      <ExperienceSection templateStyle={style}  experience={experience}         accent={accent} fonts={fonts} />
      <ProjectsSection templateStyle={style}    projects={projects}             accent={accent} fonts={fonts} />
      <SkillsSection templateStyle={style}      skills={skills}                 accent={accent} fonts={fonts} />
      <CertsSection templateStyle={style}       certifications={certifications} accent={accent} fonts={fonts} />
      <CustomSectionsSection templateStyle={style} customSections={customSections} accent={accent} fonts={fonts} />
    </Page>
  );
}

// ─── Template: SIDEBAR ────────────────────────────────────────────────────────
function SidebarTemplate({ data, accent, fonts }: TemplateProps) {
  const templateStyle = 'sidebar';
  const { personal, education, experience, projects, skills, certifications, customSections } = data;
  const sidebarSkillText = (label: string, val: string) => val ? (
    <View style={{ marginBottom: 5 }} key={label}>
      <Text style={{ fontSize: 8, fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginBottom: 1 }}>{label}</Text>
      <Text style={{ fontSize: 8.5, color: 'rgba(255,255,255,0.92)', fontFamily: fonts.body, lineHeight: 1.4 }}>{val}</Text>
    </View>
  ) : null;

  return (
    <Page size="A4" style={{ fontFamily: fonts.body, flexDirection: 'row', backgroundColor: '#fff' }}>
      {/* Left sidebar */}
      <View style={{ width: 160, backgroundColor: accent, padding: 20, flexDirection: 'column' }}>
        {personal.photo ? (
          <View style={{ width: 70, height: 70, borderRadius: 35, overflow: 'hidden', alignSelf: 'center', marginBottom: 10 }}>
          <Image src={personal.photo} style={{ width: 70, height: 70 }} />
        </View>
        ) : null}
        <Text style={{ fontSize: 16, fontFamily: fonts.heading, fontWeight: 800, color: '#fff', textAlign: 'center', marginBottom: 10, lineHeight: 1.2 }}>
          {personal.name || 'Your Name'}
        </Text>
        {/* Contact
            NOTE: do NOT use emoji icons here — the built-in PDF fonts
            (Helvetica / Times-Roman / Courier) have no glyphs for them and
            react-pdf renders them as garbage (e.g. "=R1", "=Ban"). Use only
            characters in the WinAnsi set such as "•" (used by BulletList). */}
        <View style={{ marginBottom: 7 }}>
          {personal.email     && <Text style={{ fontSize: 7.5, color: 'rgba(255,255,255,0.85)', marginBottom: 3, fontFamily: fonts.body }}>•  {personal.email}</Text>}
          {personal.phone     && <Text style={{ fontSize: 7.5, color: 'rgba(255,255,255,0.85)', marginBottom: 3, fontFamily: fonts.body }}>•  {personal.phone}</Text>}
          {personal.location  && <Text style={{ fontSize: 7.5, color: 'rgba(255,255,255,0.85)', marginBottom: 3, fontFamily: fonts.body }}>•  {personal.location}</Text>}
          {personal.linkedin  && <Text style={{ fontSize: 7.5, color: 'rgba(255,255,255,0.85)', marginBottom: 3, fontFamily: fonts.body }}>in  {personal.linkedin}</Text>}
          {personal.github    && <Text style={{ fontSize: 7.5, color: 'rgba(255,255,255,0.85)', marginBottom: 3, fontFamily: fonts.body }}>gh  {personal.github}</Text>}
          {personal.portfolio && <Text style={{ fontSize: 7.5, color: 'rgba(255,255,255,0.85)', marginBottom: 3, fontFamily: fonts.body }}>web {personal.portfolio}</Text>}
        </View>
        {/* Skills in sidebar */}
        {(skills.languages || skills.frameworks || skills.databases || skills.cloud || skills.technical || skills.soft) && (
          <View>
            <Text style={{ fontSize: 7.5, fontWeight: 700, color: 'rgba(255,255,255,0.55)', letterSpacing: 1, textTransform: 'uppercase', borderBottomWidth: 0.5, borderBottomColor: 'rgba(255,255,255,0.2)', paddingBottom: 3, marginBottom: 6 }}>SKILLS</Text>
            {sidebarSkillText('Languages',   skills.languages)}
            {sidebarSkillText('Frameworks',  skills.frameworks)}
            {sidebarSkillText('Databases',   skills.databases)}
            {sidebarSkillText('Cloud & Tools', skills.cloud)}
            {sidebarSkillText('Other',       skills.technical)}
            {sidebarSkillText('Soft Skills', skills.soft)}
          </View>
        )}
        {/* Certs in sidebar */}
        {certifications.some(c => c.name) && (
          <View style={{ marginTop: 8 }}>
            <Text style={{ fontSize: 7.5, fontWeight: 700, color: 'rgba(255,255,255,0.55)', letterSpacing: 1, textTransform: 'uppercase', borderBottomWidth: 0.5, borderBottomColor: 'rgba(255,255,255,0.2)', paddingBottom: 3, marginBottom: 6 }}>CERTIFICATIONS</Text>
            {certifications.filter(c => c.name).map((c, i) => (
              <View key={i} style={{ marginBottom: 5 }}>
                <Text style={{ fontSize: 8, fontWeight: 700, color: '#fff', fontFamily: fonts.body }}>{c.name}</Text>
                {c.issuer && <Text style={{ fontSize: 7.5, color: 'rgba(255,255,255,0.8)', fontFamily: fonts.body }}>{c.issuer}</Text>}
                {c.date   && <Text style={{ fontSize: 7, color: 'rgba(255,255,255,0.65)', fontFamily: fonts.body }}>{c.date}</Text>}
              </View>
            ))}
          </View>
        )}
      </View>

      {/* Right content */}
      <View style={{ flex: 1, paddingHorizontal: 28, paddingVertical: 32 }}>
        {personal.summary && (
          <View style={{ marginBottom: 7 }}>
            <SectionHeading label="Summary" accent={accent} fonts={fonts} templateStyle={templateStyle} />
            <Text style={{ fontSize: 9.5, color: '#333', fontFamily: fonts.body, lineHeight: 1.4 }}>{personal.summary}</Text>
          </View>
        )}
        <EducationSection templateStyle={templateStyle}  education={education}   accent={accent} fonts={fonts} />
        <ExperienceSection templateStyle={templateStyle} experience={experience} accent={accent} fonts={fonts} />
        <ProjectsSection templateStyle={templateStyle}   projects={projects}     accent={accent} fonts={fonts} />
        <CustomSectionsSection templateStyle={templateStyle} customSections={customSections} accent={accent} fonts={fonts} />
      </View>
    </Page>
  );
}

// ─── Template: BOLD ───────────────────────────────────────────────────────────
function BoldTemplate({ data, accent, fonts }: TemplateProps) {
  const templateStyle = 'bold';
  const { personal, education, experience, projects, skills, certifications, customSections } = data;
  const contactItems = [
    personal.email    && { type: 'email' as const,    value: personal.email },
    personal.phone    && { type: 'phone' as const,    value: personal.phone },
    personal.location && { type: 'location' as const, value: personal.location },
    personal.linkedin && { type: 'linkedin' as const, value: personal.linkedin },
    personal.github   && { type: 'github' as const,   value: personal.github },
    personal.portfolio && { type: 'portfolio' as const, value: personal.portfolio },
  ].filter(Boolean) as { type: ContactType; value: string }[];

  return (
    <Page size="A4" style={{ fontFamily: fonts.body, backgroundColor: '#fff', paddingTop: 16, paddingBottom: 16, paddingHorizontal: 32 }}>
      {/* Full-width coloured header.
          Negative margins cancel the Page padding so the coloured band stays
          full-bleed (edge-to-edge) despite the Page padding. */}
      <View style={{ backgroundColor: accent, paddingHorizontal: 32, paddingTop: 16, paddingBottom: 14, marginTop: -16, marginHorizontal: -32 }}>
        <View style={{ flexDirection: personal.photo ? 'row' : 'column', alignItems: personal.photo ? 'center' : 'flex-start', gap: personal.photo ? 16 : 0, marginBottom: 8 }}>
          {personal.photo ? (
            <View style={{ width: 72, height: 72, borderRadius: 36, overflow: 'hidden', flexShrink: 0 }}>
          <Image src={personal.photo} style={{ width: 72, height: 72 }} />
        </View>
          ) : null}
          <View style={personal.photo ? { flex: 1 } : {}}>
            <Text style={{ fontSize: 22, fontFamily: fonts.heading, fontWeight: 900, color: '#fff', lineHeight: 1.1, marginBottom: 4 }}>
              {personal.name || 'Your Name'}
            </Text>
            <ContactLine items={contactItems} fonts={fonts} color={withAlpha('#ffffff', 0.85)} fontSize={9.5} />
          </View>
        </View>
        {personal.summary ? (
          <View style={{ borderTopWidth: 1, borderTopColor: withAlpha('#ffffff', 0.25), paddingTop: 8 }}>
            <Text style={{ fontSize: 10.5, color: withAlpha('#ffffff', 0.9), fontFamily: fonts.body, lineHeight: 1.45 }}>{personal.summary}</Text>
          </View>
        ) : null}
      </View>

      {/* Body — no padding here. The Page-level paddingTop/Left/Right gives
          every page (including overflow pages) the same 16pt top margin so
          the first section on a continuation page isn't flush against the
          page edge. */}
      <View>
        <EducationSection templateStyle={templateStyle}   education={education}           accent={accent} fonts={fonts} />
        <ExperienceSection templateStyle={templateStyle}  experience={experience}         accent={accent} fonts={fonts} />
        <ProjectsSection templateStyle={templateStyle}    projects={projects}             accent={accent} fonts={fonts} />
        <SkillsSection templateStyle={templateStyle}      skills={skills}                 accent={accent} fonts={fonts} />
        <CertsSection templateStyle={templateStyle}       certifications={certifications} accent={accent} fonts={fonts} />
        <CustomSectionsSection templateStyle={templateStyle} customSections={customSections} accent={accent} fonts={fonts} />
      </View>
    </Page>
  );
}

// ─── Template: ELEGANT ────────────────────────────────────────────────────────
function ElegantTemplate({ data, accent, fonts }: TemplateProps) {
  const templateStyle = 'elegant';
  const { personal, education, experience, projects, skills, certifications, customSections } = data;
  const contactItems = [personal.email, personal.phone, personal.location, personal.linkedin, personal.github, personal.portfolio].filter(Boolean);

  return (
    <Page size="A4" style={{ fontFamily: fonts.body, paddingHorizontal: 52, paddingVertical: 44, backgroundColor: '#fff' }}>
      {/* Centered header */}
      <View style={{ alignItems: 'center', marginBottom: 16 }}>
        {personal.photo ? (
          <View style={{ width: 80, height: 80, borderRadius: 40, overflow: 'hidden', marginBottom: 10 }}>
          <Image src={personal.photo} style={{ width: 80, height: 80 }} />
        </View>
        ) : (
          <View style={{ marginBottom: personal.summary ? 0 : 0 }} />
        )}
        <Text style={{ fontSize: 30, fontFamily: fonts.heading, fontWeight: 700, color: accent, fontStyle: 'italic', marginBottom: 6, textAlign: 'center' }}>
          {personal.name || 'Your Name'}
        </Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', marginBottom: 12 }}>
          {contactItems.map((item, i) => (
            <Text key={i} style={{ fontSize: 12, color: '#555', fontFamily: fonts.body }}>
              {item}{i < contactItems.length - 1 ? '  ·  ' : ''}
            </Text>
          ))}
        </View>
        {personal.summary ? (
          <View style={{ borderTopWidth: 1.5, borderBottomWidth: 1.5, borderColor: accent, paddingVertical: 8, marginTop: 10, marginBottom: 10, paddingHorizontal: 16, alignItems: 'center', width: '100%' }}>
            <Text style={{ fontSize: 13, color: '#333', fontFamily: fonts.body, lineHeight: 1.6, textAlign: 'center' }}>{personal.summary}</Text>
          </View>
        ) : null}
      </View>
      <EducationSection templateStyle={templateStyle}  education={education}           accent={accent} fonts={fonts} />
      <ExperienceSection templateStyle={templateStyle} experience={experience}         accent={accent} fonts={fonts} />
      <ProjectsSection templateStyle={templateStyle}   projects={projects}             accent={accent} fonts={fonts} />
      <SkillsSection templateStyle={templateStyle}     skills={skills}                 accent={accent} fonts={fonts} />
      <CertsSection templateStyle={templateStyle}      certifications={certifications} accent={accent} fonts={fonts} />
      <CustomSectionsSection templateStyle={templateStyle} customSections={customSections} accent={accent} fonts={fonts} />
    </Page>
  );
}

// ─── Main Document component ──────────────────────────────────────────────────
function ResumeDocument({ data, templateId, customColor, fontStyleId = 'template' }: ResumeDocumentProps) {
  const template = TEMPLATES.find(t => t.id === templateId) || TEMPLATES[0];
  const accent = customColor || template.accent;
  const fonts = getFonts(templateId, fontStyleId);
  const { style } = template;

  if (style === 'sidebar')  return <Document><SidebarTemplate  data={data} accent={accent} fonts={fonts} /></Document>;
  if (style === 'bold')     return <Document><BoldTemplate     data={data} accent={accent} fonts={fonts} /></Document>;
  if (style === 'elegant')  return <Document><ElegantTemplate  data={data} accent={accent} fonts={fonts} /></Document>;

  return (
    <Document>
      <DefaultTemplate data={data} accent={accent} fonts={fonts} style={style} />
    </Document>
  );
}

// ─── Pre-crop photo to a square PNG using canvas ─────────────────────────────
// react-pdf cannot clip images to circles — overflow:hidden+borderRadius is ignored.
// Solution: crop the source image to a square on a canvas BEFORE passing to react-pdf,
// so the image is already square and borderRadius on the View just rounds the corners visually.
function cropToSquare(src: string, sizePx = 200): Promise<string> {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width  = sizePx;
      canvas.height = sizePx;
      const ctx = canvas.getContext('2d')!;
      // Center-crop: take the largest square from the center of the image
      const side   = Math.min(img.naturalWidth, img.naturalHeight);
      const sx     = (img.naturalWidth  - side) / 2;
      const sy     = (img.naturalHeight - side) / 2;
      ctx.drawImage(img, sx, sy, side, side, 0, 0, sizePx, sizePx);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(src); // fallback to original on error
    img.src = src;
  });
}

// ─── PDF generation helpers ──────────────────────────────────────────────────
// Generates the resume PDF as a Blob. Exposed separately so the in-browser
// preview can render the *exact* same PDF that downloadResumePDF produces,
// without duplicating the photo-crop / dynamic import logic.
export async function generateResumePDFBlob(
  form: ResumeFormData,
  templateId: string,
  customColor: string | null,
  fontStyleId: string = 'template',
): Promise<Blob> {
  const { pdf: pdfRenderer } = await import('@react-pdf/renderer');

  // Pre-process photo: crop to square so react-pdf renders it without distortion
  let processedForm = form;
  if (form.personal.photo) {
    const squarePhoto = await cropToSquare(form.personal.photo, 300);
    processedForm = {
      ...form,
      personal: { ...form.personal, photo: squarePhoto },
    };
  }

  return await pdfRenderer(
    <ResumeDocument
      data={processedForm}
      templateId={templateId}
      customColor={customColor}
      fontStyleId={fontStyleId}
    />
  ).toBlob();
}

// ─── Download helper ──────────────────────────────────────────────────────────
export async function downloadResumePDF(
  form: ResumeFormData,
  templateId: string,
  customColor: string | null,
  fontStyleId = 'template',
): Promise<void> {
  const blob = await generateResumePDFBlob(form, templateId, customColor, fontStyleId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(form.personal.name || 'resume').replace(/\s+/g, '_')}_resume.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export default ResumeDocument;
